/**
 * BODY COMPOSITION FORM - Quick entry modal for logging weight, waist, body fat
 */

import React, { useState } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BodyCompFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BodyCompForm: React.FC<BodyCompFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    weight_kg: '',
    waist_cm: '',
    body_fat_pct: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      // Submit metrics individually to health-metrics endpoint
      const metrics = [];

      if (formData.weight_kg) {
        metrics.push({
          date: formData.date,
          metric_type: 'weight',
          value: parseFloat(formData.weight_kg),
          unit: 'kg',
          source: 'manual'
        });
      }

      if (formData.waist_cm) {
        metrics.push({
          date: formData.date,
          metric_type: 'waist_cm',
          value: parseFloat(formData.waist_cm),
          unit: 'cm',
          source: 'manual'
        });
      }

      if (formData.body_fat_pct) {
        metrics.push({
          date: formData.date,
          metric_type: 'body_fat',
          value: parseFloat(formData.body_fat_pct),
          unit: '%',
          source: 'manual'
        });
      }

      if (metrics.length === 0) {
        setError('Please enter at least one measurement');
        return;
      }

      // Submit all metrics
      await Promise.all(
        metrics.map(metric => apiClient.post('/api/health-metrics/', metric))
      );

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Error creating body comp metrics:', err);
      setError(err.response?.data?.error || 'Failed to log body composition');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      weight_kg: '',
      waist_cm: '',
      body_fat_pct: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: COLORS.bg.panel,
        border: `1px solid ${COLORS.border.default}`,
        borderRadius: RADIUS,
        padding: SPACING[32],
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: SPACING[24]
        }}>
          <h2 style={{
            fontSize: TYPOGRAPHY.size.xl,
            fontWeight: 600,
            color: COLORS.text.primary,
            margin: 0
          }}>
            Log Body Composition
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: COLORS.text.secondary,
              padding: SPACING[8]
            }}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {error && (
          <div style={{
            padding: SPACING[16],
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid rgb(239, 68, 68)`,
            borderRadius: RADIUS,
            color: COLORS.status.error,
            marginBottom: SPACING[24],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Weight (kg)
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.weight_kg}
              onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
              min="0"
              placeholder="75.0"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              marginTop: SPACING[8]
            }}>
              Daily measurement recommended
            </div>
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Waist (cm, optional)
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.waist_cm}
              onChange={(e) => setFormData({ ...formData, waist_cm: e.target.value })}
              min="0"
              placeholder="85.0"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              marginTop: SPACING[8]
            }}>
              Weekly measurement recommended
            </div>
          </div>

          <div style={{ marginBottom: SPACING[32] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Body Fat % (optional)
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.body_fat_pct}
              onChange={(e) => setFormData({ ...formData, body_fat_pct: e.target.value })}
              min="0"
              max="100"
              placeholder="15.0"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              marginTop: SPACING[8]
            }}>
              From scale or body composition analysis
            </div>
          </div>

          <div style={{
            padding: SPACING[16],
            background: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            marginBottom: SPACING[32]
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              marginBottom: SPACING[12]
            }}>
              <strong>Tips:</strong>
            </div>
            <ul style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              margin: 0,
              paddingLeft: SPACING[24],
              listStyle: 'disc'
            }}>
              <li>Weigh yourself at the same time each day (morning recommended)</li>
              <li>Measure waist at belly button level, relaxed</li>
              <li>Body fat % is optional - use only if you have reliable measurements</li>
            </ul>
          </div>

          <div style={{
            display: 'flex',
            gap: SPACING[16],
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.secondary,
                cursor: 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.accent.default,
                border: 'none',
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500,
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Log Measurements'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BodyCompForm;
